import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

const PrivacyPolicy = () => {
  const sections = [
    {
      title: "1. Collection of Personal Information",
      content: `We collect personally identifiable information, such as name, surname, email address, and
      job title. If you make a purchase, we also collect billing and credit card information.
      Additionally, we may collect anonymous demographic data, such as age, gender, and
      location. Any information shared in public forums on Nodefleet may be viewed and used by
      others.`,
    },
    {
      title: "2. Use of Information",
      content: `We use your information to operate our website, provide the requested products and
      services, respond to inquiries, and offer customer support. We also use it to personalize
      content, prevent fraud, and improve website functionality.`,
    },
    {
      title: "3. Disclosure of Information",
      content: `We do not sell your personal information. However, we may share it with trusted service
      providers who assist us with operations such as customer support, marketing, and payment
      processing. We may also disclose information as required by law or to protect our rights.`,
    },
    {
      title: "4. User Behavior Tracking",
      content: `We may track user behavior within our website to enhance our services and provide
      personalized content.`,
    },
    {
      title: "5. Automatically Collected Information",
      content: `We automatically collect information about your device, such as IP address, browser type,
      and visited pages, to improve the user experience.`,
    },
    {
      title: "6. Use of Cookies",
      content: `We use cookies to store information on your device and facilitate future visits. You can
      manage or disable cookies in your browser settings, but this may affect site functionality.`,
    },
    {
      title: "7. Third-Party Links",
      content: `Our site may contain links to other websites. We are not responsible for their privacy
      practices and recommend reading their policies before providing personal information.`,
    },
    {
      title: "8. Information Security",
      content: `We implement security measures such as encryption and firewalls to protect your
      information. Although no system is completely secure, we continuously work to improve our
      security practices.`,
    },
    {
      title: "9. Children's Information",
      content: `We do not knowingly collect information from children under 13 years old. If you are under
      13, please obtain permission from a parent or guardian before using our services.`,
    },
    {
      title: "10. GDPR Compliance and International Transfers",
      content: `If you reside in the EU or the UK, your information may be transferred to other countries
      with different privacy laws. We ensure that these transfers comply with applicable
      regulations.`,
    },
    {
      title: "11. Your Rights",
      content: `You have the right to access, correct, delete, or restrict the processing of your personal
      information. You can also request data portability and withdraw your consent at any time.`,
    },
    {
      title: "12. Data Retention",
      content: `We retain your information as long as necessary for the purposes described in this policy,
      typically up to seven years. Thereafter, we delete or anonymize it unless required by law.`,
    },
    {
      title: "13. Email Communications",
      content: `We may send you emails with announcements, promotions, and other communications. You
      can opt out at any time by clicking "Unsubscribe" in the emails.`,
    },
    {
      title: "14. Changes to the Privacy Policy",
      content: `We reserve the right to modify this policy at any time. We will notify significant changes via
      email or through a notice on our website. Continued use of the Services after the effective
      date of the updated policy constitutes acceptance of the changes.`,
    },
    {
      title: "15. Contact",
      content: `Questions about this Privacy Policy? Reach us at:

      Nodefleet LLC
      Email: nodes@nodefleet.net
      Mailing address: 1111B S Governors Ave # 40081, Dover, DE 19904-6903, United States.`,
    },
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      {/* Hero — radial cyan + morado mesh, matches /terms */}
      <div className="relative h-[40vh] min-h-[300px] overflow-hidden flex items-center justify-center">
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(circle at 30% 30%, rgba(73,183,248,0.18) 0%, rgba(0,0,0,0) 60%), radial-gradient(circle at 70% 70%, rgba(115,103,240,0.18) 0%, rgba(0,0,0,0) 60%), #0a0a0a",
          }}
        />
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="relative z-10 text-center px-4 max-w-3xl mx-auto"
        >
          <h1 className="font-title text-4xl md:text-5xl font-bold text-white mb-4">
            Privacy <span className="text-primary">Policy</span>
          </h1>
          <p className="font-secondary text-base md:text-lg text-gray-300 max-w-2xl mx-auto">
            How Nodefleet LLC collects, uses, and protects your information when you use the
            DataNodes RPC + REST gateway and related services.
          </p>
        </motion.div>
      </div>

      {/* Body */}
      <div className="bg-landing-section">
        <motion.div
          className="max-w-4xl mx-auto px-4 md:px-8 py-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="bg-[#16142D]/60 border border-white/[0.06] rounded-sm p-6 md:p-10">
            <p className="text-gray-400 text-sm mb-2">Last updated: August 22, 2024</p>
            <p className="text-gray-300 mb-8">
              At Nodefleet LLC, protecting your information is our priority. This Privacy Policy
              applies to Nodefleet and the products and services provided by Nodefleet LLC. By
              using our website or products, you agree to the data practices described in this
              policy.
            </p>

            {sections.map((section, index) => (
              <motion.div
                key={index}
                className="mb-8"
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.35, delay: index * 0.04 }}
              >
                <h2 className="font-title text-xl md:text-2xl font-semibold text-white mb-3">
                  {section.title}
                </h2>
                <p className="font-secondary text-gray-300 leading-relaxed text-sm md:text-base whitespace-pre-line">
                  {section.content}
                </p>
              </motion.div>
            ))}

            <div className="mt-12 border-t border-white/[0.06] pt-6 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
              <p className="text-xs text-gray-500">
                Questions? Email{" "}
                <a
                  href="mailto:nodes@nodefleet.net"
                  className="text-primary underline underline-offset-2 hover:text-primary-hover"
                >
                  nodes@nodefleet.net
                </a>
                .
              </p>
              <div className="flex gap-3 text-xs text-gray-400">
                <Link to="/terms" className="hover:text-white">Terms of Service</Link>
                <span className="text-gray-600">·</span>
                <Link to="/" className="hover:text-white">Back to home</Link>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
